import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { PageHeader, Card } from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Save, Palette, Check, ShieldCheck, ShieldOff, Mail, Moon, Layers, Cpu, Sun, CreditCard, Zap, AlertTriangle, ExternalLink, Send, Bell } from 'lucide-react';

const TEMA_IKON = {
  default: Moon,
  modern:  Layers,
  primary: Cpu,
  light:   Sun,
};

const Innstillinger = () => {
  const { tema, setTema, temaer } = useTheme();
  const { setFirma: setAuthFirma } = useAuth();
  const [firma, setFirma] = useState(null);
  const [melding, setMelding] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/firma').then((r) => {
      setFirma(r.data);
      // Merk: vi overstyrer IKKE lokalt valgt tema fra serveren her.
      // Tema synkroniseres ved login (AuthContext) og lagres lokalt+remote ved bytte.
    });
    // eslint-disable-next-line
  }, []);

  const lagre = async () => {
    setBusy(true); setMelding('');
    try {
      await api.put('/firma', firma);
      if (setAuthFirma) setAuthFirma(firma);
      setMelding('Lagret.');
      setTimeout(() => setMelding(''), 2000);
    } catch { setMelding('Klarte ikke lagre.'); }
    finally { setBusy(false); }
  };

  if (!firma) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader title="Innstillinger" />

      <Card style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Palette size={13} /> Tema
        </h3>
        <div className="flex" style={{ flexWrap: 'wrap', gap: 6 }}>
          {temaer.map((t) => {
            const Icon = TEMA_IKON[t.id] || Palette;
            const valgt = tema === t.id;
            return (
              <button key={t.id} type="button"
                      onClick={() => { setTema(t.id); setFirma({ ...firma, tema: t.id }); }}
                      title={t.navn}
                      style={{
                        height: 28, padding: '0 10px', borderRadius: 6,
                        border: `1px solid ${valgt ? 'var(--theme-accent)' : 'var(--theme-border)'}`,
                        background: valgt ? 'var(--theme-accent)' : 'var(--theme-bg-card)',
                        color: valgt ? '#fff' : 'var(--theme-text)',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      }}>
                <Icon size={12} />
                <span>{t.navn}</span>
                {valgt && <Check size={12} />}
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>Firmainformasjon</h3>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Logo</label>
            <div className="flex items-center" style={{ gap: 12 }}>
              {firma.logo ? (
                <img src={firma.logo} alt="logo"
                     style={{ maxHeight: 56, maxWidth: 180, border: '1px solid var(--theme-border)',
                              borderRadius: 4, padding: 4, background: '#fff' }} />
              ) : (
                <span className="fm-text-muted" style={{ fontSize: 11 }}>Ingen logo lastet opp</span>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 512 * 1024) { alert('Maks 512 kB.'); return; }
                  const r = new FileReader();
                  r.onload = () => setFirma({ ...firma, logo: r.result });
                  r.readAsDataURL(f);
                }}
              />
              {firma.logo && (
                <button className="btn-ghost" onClick={() => setFirma({ ...firma, logo: '' })}
                        style={{ color: 'var(--theme-danger)' }}>Fjern</button>
              )}
            </div>
            <p className="fm-text-muted" style={{ fontSize: 10, margin: '4px 0 0' }}>
              Vises øverst på faktura og tilbud (PNG/JPG/SVG, maks 512 kB).
            </p>
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label>Firmanavn</label><input value={firma.navn || ''} onChange={(e) => setFirma({...firma, navn: e.target.value})} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Adresse</label><input value={firma.adresse || ''} onChange={(e) => setFirma({...firma, adresse: e.target.value})} /></div>
          <div><label>Postnr</label><input value={firma.postnr || ''} onChange={(e) => setFirma({...firma, postnr: e.target.value})} /></div>
          <div><label>Sted</label><input value={firma.sted || ''} onChange={(e) => setFirma({...firma, sted: e.target.value})} /></div>
          <div><label>Org.nr</label><input value={firma.orgnr || ''} onChange={(e) => setFirma({...firma, orgnr: e.target.value})} /></div>
          <div><label>Telefon</label><input value={firma.telefon || ''} onChange={(e) => setFirma({...firma, telefon: e.target.value})} /></div>
          <div><label>E-post</label><input type="email" value={firma.epost || ''} onChange={(e) => setFirma({...firma, epost: e.target.value})} /></div>
          <div><label>Kontonummer</label><input value={firma.kontonr || ''} onChange={(e) => setFirma({...firma, kontonr: e.target.value})} /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="mva" checked={!!firma.mva_pliktig} onChange={(e) => setFirma({...firma, mva_pliktig: e.target.checked})} />
            <label htmlFor="mva" style={{ margin: 0 }}>MVA-pliktig</label>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="er_forening" checked={!!firma.er_forening}
                   onChange={(e) => setFirma({...firma, er_forening: e.target.checked})} />
            <label htmlFor="er_forening" style={{ margin: 0 }}>
              Aktiver <strong>forenings-modus</strong> (medlemsregister + grupper/verv)
            </label>
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 10, marginTop: 16 }}>
          <button className="btn-primary" onClick={lagre} disabled={busy}><Save size={14} /> {busy ? 'Lagrer…' : 'Lagre'}</button>
          {melding && <span className="badge badge-success">{melding}</span>}
        </div>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={14} /> E-post (SMTP)
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
          Konfigurer SMTP for å sende fakturaer og purringer på e-post. La passordfeltet være tomt for å beholde eksisterende passord.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div><label>SMTP-server (host)</label>
            <input value={firma.smtp_host || ''} onChange={(e) => setFirma({ ...firma, smtp_host: e.target.value })} placeholder="smtp.gmail.com" /></div>
          <div><label>Port</label>
            <input type="number" value={firma.smtp_port ?? 587} onChange={(e) => setFirma({ ...firma, smtp_port: parseInt(e.target.value, 10) || 587 })} /></div>
          <div><label>Brukernavn</label>
            <input value={firma.smtp_bruker || ''} onChange={(e) => setFirma({ ...firma, smtp_bruker: e.target.value })} /></div>
          <div><label>Passord {firma.smtp_passord_satt && <span className="badge badge-success" style={{ fontSize: 9 }}>satt</span>}</label>
            <input type="password" value={firma.smtp_passord || ''} onChange={(e) => setFirma({ ...firma, smtp_passord: e.target.value })}
                   placeholder={firma.smtp_passord_satt ? '••••••••' : ''} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Avsender-adresse (From)</label>
            <input type="email" value={firma.smtp_avsender || ''} onChange={(e) => setFirma({ ...firma, smtp_avsender: e.target.value })}
                   placeholder="post@firmaet.no" /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="smtp_tls" checked={firma.smtp_tls !== false}
                   onChange={(e) => setFirma({ ...firma, smtp_tls: e.target.checked })} />
            <label htmlFor="smtp_tls" style={{ margin: 0 }}>Bruk STARTTLS / SSL (anbefalt)</label>
          </div>
        </div>
      </Card>

      {firma.er_forening && (
        <Card style={{ marginTop: 12 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Bell size={14} /> Varsling for foreningen (Slack / Discord)
          </h3>
          <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
            Lim inn en <b>Incoming Webhook</b>-URL fra Slack eller Discord for å få varsler
            i kanalen om nye donasjoner og medlemmer. Webhook-URLene er per forening og
            settes av styret/admin selv (ikke plattform-secrets).
          </p>
          <div className="grid" style={{ gridTemplateColumns: '1fr', gap: 12 }}>
            <div>
              <label>Slack webhook URL</label>
              <input value={firma.forening_slack_webhook || ''}
                     onChange={(e) => setFirma({ ...firma, forening_slack_webhook: e.target.value })}
                     placeholder="https://hooks.slack.com/services/..." />
            </div>
            <div>
              <label>Discord webhook URL</label>
              <input value={firma.forening_discord_webhook || ''}
                     onChange={(e) => setFirma({ ...firma, forening_discord_webhook: e.target.value })}
                     placeholder="https://discord.com/api/webhooks/..." />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="varsle_donasjon" checked={!!firma.forening_varsle_donasjon}
                     onChange={(e) => setFirma({ ...firma, forening_varsle_donasjon: e.target.checked })} />
              <label htmlFor="varsle_donasjon" style={{ margin: 0 }}>Varsle om nye donasjoner</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="varsle_medlem" checked={!!firma.forening_varsle_medlem}
                     onChange={(e) => setFirma({ ...firma, forening_varsle_medlem: e.target.checked })} />
              <label htmlFor="varsle_medlem" style={{ margin: 0 }}>Varsle om nye medlemmer</label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="btn-ghost" type="button"
                    disabled={!firma.forening_slack_webhook}
                    onClick={async () => {
                      try {
                        const r = await api.post('/forening/varsling/test', { kanal: 'slack', tekst: 'Test fra Regnino ✓' });
                        alert(r.data.ok ? 'Slack-test sendt!' : ('Feil: ' + (r.data.feil || '')));
                      } catch (e) { alert('Feil: ' + (e.response?.data?.error || e.message)); }
                    }}>
              <Send size={13} /> Test Slack
            </button>
            <button className="btn-ghost" type="button"
                    disabled={!firma.forening_discord_webhook}
                    onClick={async () => {
                      try {
                        const r = await api.post('/forening/varsling/test', { kanal: 'discord', tekst: 'Test fra Regnino ✓' });
                        alert(r.data.ok ? 'Discord-test sendt!' : ('Feil: ' + (r.data.feil || '')));
                      } catch (e) { alert('Feil: ' + (e.response?.data?.error || e.message)); }
                    }}>
              <Send size={13} /> Test Discord
            </button>
            <span className="fm-text-muted" style={{ fontSize: 11, alignSelf: 'center' }}>
              Husk å lagre webhook-URL øverst før du tester.
            </span>
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 12 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Cpu size={14} /> AI / OCR (OpenAI)
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 11, margin: 0 }}>
          API-nøkkel og modell konfigureres på <a href="/admin">Admin-siden</a> (kun synlig for firma-eier).
        </p>
      </Card>

      <Abonnement />

      <Gdpr />

      <ToFaktor />
    </>
  );
};

const Abonnement = () => {
  const [data, setData] = useState(null);
  const [feil, setFeil] = useState('');
  const [busy, setBusy] = useState('');

  const last = () => api.get('/abonnement').then(r => setData(r.data)).catch(e =>
    setFeil(e.response?.data?.error || 'Kunne ikke hente abonnement.'));

  useEffect(() => { last(); }, []);

  const bytt = async (planId) => {
    setFeil(''); setBusy('checkout');
    try {
      const r = await api.post('/abonnement/checkout', { plan: planId });
      if (r.data.url) window.location = r.data.url;
    } catch (e) { setFeil(e.response?.data?.error || 'Stripe-feil.'); }
    finally { setBusy(''); }
  };

  const portal = async () => {
    setFeil(''); setBusy('portal');
    try {
      const r = await api.post('/abonnement/portal');
      if (r.data.url) window.location = r.data.url;
    } catch (e) { setFeil(e.response?.data?.error || 'Stripe-feil.'); }
    finally { setBusy(''); }
  };

  const vipps = async (planId) => {
    setFeil(''); setBusy('vipps');
    try {
      const r = await api.post('/abonnement/vipps', { plan: planId });
      if (r.data.url) window.location = r.data.url;
    } catch (e) { setFeil(e.response?.data?.error || 'Vipps-feil.'); }
    finally { setBusy(''); }
  };

  const vippsFast = async (planId) => {
    setFeil(''); setBusy('vipps_rec');
    try {
      const r = await api.post('/abonnement/vipps-recurring', { plan: planId });
      if (r.data.url) window.location = r.data.url;
    } catch (e) { setFeil(e.response?.data?.error || 'Vipps-feil.'); }
    finally { setBusy(''); }
  };

  const vippsAvbryt = async () => {
    if (!window.confirm('Stopp Vipps-avtalen for faste trekk?')) return;
    setFeil(''); setBusy('vipps_avbryt');
    try {
      await api.post('/abonnement/vipps-recurring/avbryt');
      hent();
    } catch (e) { setFeil(e.response?.data?.error || 'Vipps-feil.'); }
    finally { setBusy(''); }
  };

  const settMetode = async (m) => {
    setFeil(''); setBusy('metode');
    try {
      const r = await api.post('/abonnement/betalingsmetode', { metode: m });
      setData(d => ({ ...d, betalingsmetode: r.data.betalingsmetode }));
    } catch (e) { setFeil(e.response?.data?.error || 'Kunne ikke endre betalingsmetode.'); }
    finally { setBusy(''); }
  };

  if (!data) return null;

  const naa = data.plan?.id || 'free';
  const fmtKr = (n) => n === 0 ? 'Gratis' : `${n} kr/mnd`;

  return (
    <Card style={{ marginTop: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <CreditCard size={14} /> Abonnement og betaling
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 10px' }}>
        Nåværende plan: <strong>{data.plan?.navn}</strong>
        {data.plan_status && data.plan_status !== 'free' && <> · status <strong>{data.plan_status}</strong></>}
        {data.plan_renews_at && <> · fornyes {data.plan_renews_at}</>}
      </p>

      {(() => {
        const ubegr = (v) => (v >= 999999 ? '∞' : v);
        const rader = [
          { navn: 'Bilag', bruk: data.bruk?.bilag, maks: data.plan?.maks_bilag },
          { navn: 'Brukere', bruk: data.bruk?.brukere, maks: data.plan?.maks_brukere },
          { navn: 'Ansatte', bruk: data.bruk?.ansatte, maks: data.plan?.maks_ansatte },
          { navn: 'EHF denne mnd', bruk: data.bruk?.ehf_mnd, maks: data.plan?.maks_ehf_mnd },
          { navn: 'AI-skann denne mnd', bruk: data.bruk?.ai_skann_mnd, maks: data.plan?.maks_ai_skann_mnd },
          { navn: 'Bank-tilkoblinger', bruk: data.bruk?.bank, maks: data.plan?.maks_bank },
        ].filter(r => r.maks !== undefined);
        return (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 6, marginBottom: 10 }}>
            {rader.map((r) => {
              const naadd = r.maks < 999999 && r.maks > 0 && (r.bruk || 0) >= r.maks;
              const pct = r.maks > 0 && r.maks < 999999 ? Math.min(100, Math.round((r.bruk || 0) / r.maks * 100)) : 0;
              return (
                <div key={r.navn} style={{ border: '1px solid var(--theme-border)', borderRadius: 6, padding: '6px 8px' }}>
                  <div style={{ fontSize: 10, color: 'var(--theme-text-muted)' }}>{r.navn}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: naadd ? 'var(--theme-danger,#ef4444)' : 'inherit' }}>
                    {r.bruk ?? 0}<span className="fm-text-muted" style={{ fontWeight: 400, fontSize: 11 }}> / {ubegr(r.maks)}</span>
                  </div>
                  {r.maks > 0 && r.maks < 999999 && (
                    <div style={{ height: 3, background: 'var(--theme-border)', borderRadius: 2, marginTop: 3 }}>
                      <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2,
                                    background: naadd ? 'var(--theme-danger,#ef4444)' : 'var(--theme-accent)' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {!data.stripe_aktivert && (
        <div className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
          <AlertTriangle size={11} /> Stripe ikke konfigurert på server – kortbetaling deaktivert.
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        {data.planer.map((p) => {
          const aktiv = p.id === naa;
          const harPris = !!data.stripe_priser_satt?.[p.id];
          return (
            <div key={p.id} style={{
              border: `1.5px solid ${aktiv ? 'var(--theme-accent)' : 'var(--theme-border)'}`,
              borderRadius: 8, padding: 10, background: 'var(--theme-bg-card)',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 13 }}>{p.navn}</strong>
                {aktiv && <span className="badge badge-success" style={{ fontSize: 9 }}>Nåværende</span>}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{fmtKr(p.pris_mnd)}</div>
              <div className="fm-text-muted" style={{ fontSize: 10, lineHeight: 1.4 }}>
                · {p.maks_brukere >= 99999 ? '∞' : p.maks_brukere} brukere<br/>
                · {p.maks_bilag >= 99999 ? '∞' : p.maks_bilag} bilag/mnd
              </div>
              {!aktiv && p.id !== 'free' && (
                <button className="btn-primary" style={{ fontSize: 11, padding: '4px 8px' }}
                        disabled={!data.stripe_aktivert || !harPris || busy === 'checkout'}
                        onClick={() => bytt(p.id)}
                        title={!harPris ? 'Stripe Price ID mangler for denne planen.' : ''}>
                  Velg plan
                </button>
              )}
              {!aktiv && p.id !== 'free' && data.vipps_aktivert && (
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 6px' }}
                        disabled={busy === 'vipps'}
                        onClick={() => vipps(p.id)}>
                  <Zap size={10} /> Vipps (12 mnd)
                </button>
              )}
              {!aktiv && p.id !== 'free' && data.vipps_aktivert && (
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 6px' }}
                        disabled={busy === 'vipps_rec'}
                        onClick={() => vippsFast(p.id)}
                        title="Faste manedlige trekk via Vipps">
                  <Zap size={10} /> Vipps faste trekk
                </button>
              )}
            </div>
          );
        })}
      </div>

      {data.plan?.id !== 'free' && data.stripe_aktivert && (
        <div style={{ marginTop: 10 }}>
          <button className="btn-ghost" onClick={portal} disabled={busy === 'portal'} style={{ fontSize: 11 }}>
            <ExternalLink size={11} /> Administrer abonnement (Stripe)
          </button>
        </div>
      )}

      {data.plan?.id !== 'free' && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--theme-border)' }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600 }}>Betalingsmetode for fakturering</h4>
          <p className="fm-text-muted" style={{ fontSize: 10, margin: '0 0 6px' }}>
            Velg hvordan du vil få tilsendt månedlig faktura. EHF krever at firmaet ditt har orgnr og kan motta EHF via Peppol.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { v: 'manuell', n: 'Manuell (epost)' },
              { v: 'stripe',  n: 'Kort (Stripe)' },
              ...(data.vipps_aktivert ? [{ v: 'vipps', n: 'Vipps' }] : []),
              { v: 'ehf',     n: 'EHF (Peppol)' },
            ].map(opt => (
              <button key={opt.v}
                className={data.betalingsmetode === opt.v ? 'btn-primary' : 'btn-ghost'}
                style={{ fontSize: 11, padding: '4px 8px' }}
                disabled={busy === 'metode'}
                onClick={() => settMetode(opt.v)}>
                {opt.n}
              </button>
            ))}
          </div>
          {data.betalingsmetode === 'ehf' && (
            <div className="fm-text-muted" style={{ fontSize: 10, marginTop: 6 }}>
              Faktura sendes automatisk månedlig til Peppol-ID <code>0192:{data.plan?.id ? '<orgnr>' : ''}</code>.
              {data.ehf_sist_sendt && <> Sist sendt: <strong>{data.ehf_sist_sendt}</strong>.</>}
            </div>
          )}
        </div>
      )}

      {feil && <div className="badge badge-danger" style={{ marginTop: 8 }}>{feil}</div>}
    </Card>
  );
};

const Gdpr = () => {
  const [busy, setBusy] = useState('');
  const [feil, setFeil] = useState('');

  const lastNedMinData = async () => {
    setBusy('eksport'); setFeil('');
    try {
      const r = await api.get('/gdpr/min-data', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'min-data.json';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setFeil(e.response?.data?.error || 'Eksport feilet.'); }
    finally { setBusy(''); }
  };

  const anonymiser = async () => {
    if (!window.confirm('Anonymiser brukeren din permanent?\n\nRegnskapsdata beholdes pga. lovpålagt 5-års oppbevaringsplikt, men navn, e-post og passord slettes. Du blir logget ut.')) return;
    setBusy('slett'); setFeil('');
    try {
      await api.post('/gdpr/anonymiser-min-konto', { bekreft: 'JA SLETT' });
      alert('Brukeren din er anonymisert. Du blir logget ut.');
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (e) { setFeil(e.response?.data?.error || 'Anonymisering feilet.'); }
    finally { setBusy(''); }
  };

  return (
    <Card style={{ marginTop: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={14} /> Personvern (GDPR)
      </h3>
      <p className="fm-text-muted" style={{ fontSize: 11, margin: '0 0 12px' }}>
        Du har rett til å få utlevert egne data (§20) og rett til å bli glemt (§17).
        Regnskapsdata må beholdes i minst 5 år iht. bokføringsloven §13.
      </p>
      <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button className="btn-ghost" onClick={lastNedMinData} disabled={busy === 'eksport'}>
          {busy === 'eksport' ? 'Henter…' : 'Last ned mine data (JSON)'}
        </button>
        <button className="btn-ghost" onClick={anonymiser} disabled={busy === 'slett'}
                style={{ color: 'var(--theme-danger)' }}>
          {busy === 'slett' ? 'Anonymiserer…' : 'Anonymiser min konto'}
        </button>
      </div>
      {feil && <div className="badge badge-danger" style={{ marginTop: 8 }}>{feil}</div>}
    </Card>
  );
};

const ToFaktor = () => {
  const { user, refresh } = useAuth();
  const [oppsett, setOppsett] = useState(null);  // { secret, otpauth }
  const [kode, setKode] = useState('');
  const [passord, setPassord] = useState('');
  const [feil, setFeil] = useState('');

  const start = async () => {
    setFeil('');
    const r = await api.post('/auth/2fa/init');
    setOppsett(r.data);
  };

  const verifiser = async () => {
    setFeil('');
    try {
      await api.post('/auth/2fa/verify', { kode });
      setOppsett(null); setKode('');
      await refresh();
    } catch (e) { setFeil(e.response?.data?.error || 'Feil kode.'); }
  };

  const deaktiver = async () => {
    setFeil('');
    try {
      await api.post('/auth/2fa/disable', { passord });
      setPassord('');
      await refresh();
    } catch (e) { setFeil(e.response?.data?.error || 'Feil passord.'); }
  };

  return (
    <Card style={{ marginTop: 12 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={14} /> To-faktor autentisering (2FA)
      </h3>
      {user?.totp_aktivert ? (
        <>
          <p className="fm-text-muted" style={{ fontSize: 12 }}>
            <Check size={12} style={{ color: 'var(--theme-success)' }} /> 2FA er aktivert. Skriv inn passord for å deaktivere.
          </p>
          <div className="flex" style={{ gap: 8, alignItems: 'flex-end', maxWidth: 400 }}>
            <div style={{ flex: 1 }}>
              <label>Passord</label>
              <input type="password" value={passord} onChange={(e) => setPassord(e.target.value)} />
            </div>
            <button className="btn-ghost" onClick={deaktiver} style={{ color: 'var(--theme-danger)' }}>
              <ShieldOff size={12} /> Deaktiver
            </button>
          </div>
        </>
      ) : oppsett ? (
        <>
          <p className="fm-text-muted" style={{ fontSize: 12 }}>
            Skann denne URI-en med Google Authenticator / Microsoft Authenticator / Authy:
          </p>
          <div style={{ background: 'var(--theme-bg-input)', padding: 10, borderRadius: 6,
                        fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', marginBottom: 8 }}>
            {oppsett.otpauth}
          </div>
          <p className="fm-text-muted" style={{ fontSize: 11 }}>
            Eller legg til manuelt med hemmelighet: <strong>{oppsett.secret}</strong>
          </p>
          <div className="flex" style={{ gap: 8, alignItems: 'flex-end', maxWidth: 400, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label>6-sifret kode fra appen</label>
              <input value={kode} onChange={(e) => setKode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                     placeholder="123456" style={{ letterSpacing: 4, textAlign: 'center' }} />
            </div>
            <button className="btn-primary" onClick={verifiser}>Aktiver</button>
          </div>
        </>
      ) : (
        <>
          <p className="fm-text-muted" style={{ fontSize: 12 }}>
            Anbefales for sensitive regnskapsdata. Bruker TOTP (RFC 6238) – kompatibel med alle vanlige autentiseringsapper.
          </p>
          <button className="btn-primary" onClick={start}>
            <ShieldCheck size={14} /> Sett opp 2FA
          </button>
        </>
      )}
      {feil && <div className="badge badge-danger" style={{ marginTop: 8 }}>{feil}</div>}
    </Card>
  );
};

export default Innstillinger;
